import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import Button from "../../components/Buttons/Button";
import { useAuth } from "../../context/AuthContext";
import { useNotify } from "../../context/NotificationContext";
import {
  addTeacherCatalog,
  fetchPublicTeacher,
} from "../../services/studentPortalService/studentPortalService";
import { logger } from "../../utils/logger";
import {
  CardMeta,
  DetailSection,
  EmptyState,
  InlineRow,
  LoadingState,
  PageLead,
  PageTitle,
  SectionTitle,
} from "./studentPortal.styles";

const StudentCatalogLinkPage: React.FC = () => {
  const { slug = "" } = useParams();
  const { isAuthenticated, role, logout } = useAuth();
  const [teacherName, setTeacherName] = useState("");
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const navigate = useNavigate();
  const toast = useNotify();

  useEffect(() => {
    let active = true;
    fetchPublicTeacher(slug)
      .then((teacher) => {
        if (active) setTeacherName(teacher.organization_name);
      })
      .catch((error) => {
        logger.error("Не удалось открыть каталог преподавателя:", error);
        if (active) setNotFound(true);
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [slug]);

  const addCatalog = async () => {
    setAdding(true);
    try {
      const result = await addTeacherCatalog(slug);
      toast.show({
        severity: "success",
        summary: "Готово",
        detail: result.created ? "Курсы преподавателя добавлены в ваш кабинет" : "Этот каталог уже добавлен",
        life: 3000,
      });
      navigate("/student/catalog", { replace: true });
    } catch (error) {
      logger.error("Ошибка добавления каталога:", error);
      toast.show({ severity: "error", summary: "Ошибка", detail: "Не удалось добавить каталог", life: 3000 });
    } finally {
      setAdding(false);
    }
  };

  if (loading) return <LoadingState style={{ padding: 40 }}>Загружаем информацию…</LoadingState>;
  if (notFound) return <EmptyState style={{ margin: 40 }}>Ссылка преподавателя не найдена.</EmptyState>;

  const returnPath = encodeURIComponent(`/s/${slug}`);

  return (
    <div style={{ maxWidth: 720, margin: "64px auto", padding: "0 20px" }}>
      <DetailSection>
        <PageTitle>{teacherName}</PageTitle>
        <PageLead>По этой ссылке можно добавить каталог преподавателя в ваш кабинет ученика.</PageLead>
        <SectionTitle style={{ marginTop: 28 }}>Добавить курсы этого преподавателя в ваш кабинет?</SectionTitle>
        <CardMeta>
          После подтверждения каталог останется доступен в вашем кабинете. Повторно переходить по этой ссылке не потребуется. Доступ к обучению по конкретному курсу появляется только после одобрения заявки преподавателем.
        </CardMeta>

        <InlineRow style={{ marginTop: 24 }}>
          {!isAuthenticated ? (
            <>
              <Button type="button" onClick={() => navigate(`/login?role=student&next=${returnPath}`)}>
                Войти как ученик
              </Button>
              <Button type="button" variant="secondary" onClick={() => navigate(`/signup?role=student&next=${returnPath}`)}>
                Зарегистрироваться
              </Button>
            </>
          ) : role === "student" ? (
            <>
              <Button type="button" loading={adding} onClick={addCatalog}>Добавить в кабинет</Button>
              <Button type="button" variant="secondary" onClick={() => navigate("/student/catalog")}>Не добавлять</Button>
            </>
          ) : (
            <>
              <Button
                type="button"
                onClick={() => {
                  logout();
                  navigate(`/login?role=student&next=${returnPath}`);
                }}
              >
                Войти как ученик
              </Button>
              <CardMeta>Сейчас выполнен вход в аккаунт преподавателя.</CardMeta>
            </>
          )}
        </InlineRow>
      </DetailSection>
    </div>
  );
};

export default StudentCatalogLinkPage;
